import { RecordTuple } from "record-tuple";

import UFunction from "./UFunction.mts";
import UObject from "./UObject.mts";
import UProxy from "./UProxy.mts";

class PendingCache<Key = unknown, Value = unknown> {
  static readonly cacheHit = Symbol("PendingCache.cacheHit");
  private static readonly _unwrap = Symbol("PendingCache.unwrap");

  static unwrap<Fn extends UFunction.Any>(
    fn: Fn & { [PendingCache._unwrap]?: Fn }
  ): Fn {
    return fn[PendingCache._unwrap] || fn;
  }

  private cache = new Map<Key, Promise<Value>>();

  use(
    key: Key,
    create: () => Promise<Value>
  ): Promise<Value> & {
    [PendingCache.cacheHit]?: boolean;
  } {
    if (this.cache.has(key))
      return Object.assign(Promise.resolve(this.cache.get(key)!), {
        [PendingCache.cacheHit]: true,
      });

    const result = create();

    if (!(result instanceof Promise)) return result;

    this.cache.set(key, result);
    result.finally(() => {
      this.cache.delete(key);
    });

    return result;
  }

  wrap<T extends UObject.Any>(namespace: string, obj: T) {
    return UProxy.deepDecorate(obj, (action, path) =>
      Object.assign(
        (...args: any[]) =>
          this.use(PendingCache.generateKey(namespace, path, args) as Key, () =>
            action(...args)
          ),
        { [PendingCache._unwrap]: action }
      )
    );
  }
}

namespace PendingCache {
  export let generateKey = (
    namespace: string,
    path: UObject.Key[],
    args: any[]
  ) => {
    const encountered = new Set<unknown>();
    return RecordTuple.deep([
      namespace,
      path,
      args.map((arg) =>
        UObject.transform.deep(arg, ([key, value], next, obj) => {
          // Prevent circular references
          // TODO: Resolve to circular reference path if necessary
          if (encountered.has(obj)) return;
          encountered.add(obj);

          if (value instanceof Uint8Array) return [key, value.toBase64()];

          // Avoid getters, common source of circular references
          return Object.getOwnPropertyDescriptor(obj, key)?.get
            ? undefined
            : [key, next()];
        })
      ),
    ]);
  };
}

export default PendingCache;
